import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  NodeKind,
  findRenderedNode,
  imageNodesInOrder,
} from '@/core/document'
import { useEditSession } from '@/core/store/editSession'
import { ToolTab } from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import { Button } from '@/ui/shadcn/button'
import { PhotoThumbnail } from '@/ui/PhotoThumbnail'

function showsEditingPhotoContext(tab: ToolTab): boolean {
  switch (tab) {
    case ToolTab.Crop:
    case ToolTab.Adjust:
    case ToolTab.Frame:
      return true
    case ToolTab.Collage:
    case ToolTab.Format:
    case ToolTab.Export:
      return false
    default:
      throw new Error(`Unknown tool tab for active photo context: "${String(tab)}"`)
  }
}

export function ActivePhotoContext() {
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const selectedNodeId = useEditSession((state) => state.selectedNodeId)
  const activeTab = useEditSession((state) => state.activeTab)
  const selectNode = useEditSession((state) => state.selectNode)
  if (!document) throw new Error('Active photo context requires a loaded document')

  const photos = imageNodesInOrder(document.root)
  if (photos.length <= 1) return null
  if (activeTab === ToolTab.Collage) {
    return (
      <div className="shrink-0 border-b px-5 py-2 text-sm text-muted-foreground" data-testid={TEST_IDS.activePhotoContext}>
        {photos.length} photos · tap one to edit
      </div>
    )
  }
  if (!showsEditingPhotoContext(activeTab)) return null
  if (!selectedNodeId) throw new Error('Editing photo context requires an active photo')

  const activeIndex = photos.findIndex((photo) => photo.id === selectedNodeId)
  if (activeIndex < 0) throw new Error(`Active photo "${selectedNodeId}" is missing from reading order`)
  const activePhoto = photos[activeIndex]
  if (!activePhoto || activePhoto.kind !== NodeKind.Image) {
    throw new Error('Active photo context requires an Image node')
  }
  const source = sources.get(activePhoto.sourceId)
  if (!source) throw new Error(`Active photo context is missing source "${activePhoto.sourceId}"`)
  const renderedPhotos = photos.filter((photo) => findRenderedNode(document.root, photo.id) !== null)
  const renderedIndex = renderedPhotos.findIndex((photo) => photo.id === activePhoto.id)
  if (renderedIndex < 0) throw new Error(`Active photo "${activePhoto.id}" is not rendered in the composition`)
  const canSwitch = renderedPhotos.length > 1
  const selectOffset = (offset: -1 | 1) => {
    const targetIndex = (renderedIndex + offset + renderedPhotos.length) % renderedPhotos.length
    const target = renderedPhotos[targetIndex]
    if (!target) throw new Error('Active photo navigation did not resolve a rendered photo')
    selectNode(target.id)
  }

  return (
    <div className="flex shrink-0 items-center gap-2 border-b px-5 py-2" data-testid={TEST_IDS.activePhotoContext}>
      <PhotoThumbnail document={document} image={activePhoto} source={source} />
      <span className="min-w-0 flex-1 text-sm font-medium">Editing photo {activeIndex + 1} of {photos.length}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7"
        disabled={!canSwitch}
        data-testid={TEST_IDS.activePhotoPrevious}
        aria-label="Edit previous photo"
        onClick={() => selectOffset(-1)}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7"
        disabled={!canSwitch}
        data-testid={TEST_IDS.activePhotoNext}
        aria-label="Edit next photo"
        onClick={() => selectOffset(1)}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  )
}
